import { cn } from '@/lib/utils';
import { CheckCircle, Circle, Loader2, MinusCircle, XCircle } from 'lucide-react';
import type { ExecutionStep, StepStatus } from '../types';

const STEP_ICON_MAP: Record<
  StepStatus,
  { icon: typeof CheckCircle; className: string; spin?: boolean | undefined }
> = {
  completed: { icon: CheckCircle, className: 'text-green-500' },
  active: { icon: Loader2, className: 'text-blue-500', spin: true },
  failed: { icon: XCircle, className: 'text-red-500' },
  pending: { icon: Circle, className: 'text-muted-foreground' },
  skipped: { icon: MinusCircle, className: 'text-muted-foreground' },
};

function formatDuration(ms?: number | undefined): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return `${min}m ${sec}s`;
}

interface StepTimelineProps {
  steps: ExecutionStep[];
}

export function StepTimeline({ steps }: StepTimelineProps) {
  return (
    <div className="space-y-0" data-testid="flow-step-timeline">
      {steps.map((step, index) => {
        const config = STEP_ICON_MAP[step.status];
        const StepIcon = config.icon;
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <StepIcon
                className={cn('h-4 w-4 shrink-0', config.className, config.spin && 'animate-spin')}
                aria-hidden="true"
              />
              {!isLast && <div className="w-px h-4 bg-border" />}
            </div>
            <div className="flex flex-1 items-center justify-between min-h-[2rem] -mt-0.5">
              <span
                className={cn(
                  'text-xs',
                  step.status === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                )}
              >
                {step.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDuration(step.durationMs)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
